import React, { useState, useEffect } from 'react';
import { AtSign, Check, X, AlertCircle, Loader } from 'lucide-react';

const UsernameManager = ({ username, onUpdate, isEditing }) => {
  const [newUsername, setNewUsername] = useState(username);
  const [isChecking, setIsChecking] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    setNewUsername(username);
  }, [username]);

  const validateUsername = (value) => {
    const errors = [];
    
    if (!value) {
      errors?.push('Username is required');
    } else {
      if (value?.length < 3) {
        errors?.push('Username must be at least 3 characters');
      }
      if (value?.length > 20) {
        errors?.push('Username must be no more than 20 characters');
      }
      if (!/^[a-zA-Z0-9_]+$/?.test(value)) {
        errors?.push('Username can only contain letters, numbers, and underscores');
      }
      if (/^_|_$/?.test(value)) {
        errors?.push('Username cannot start or end with underscore');
      }
      if (/__/?.test(value)) {
        errors?.push('Username cannot contain consecutive underscores');
      }
    }

    return errors;
  };

  const checkAvailability = async (value) => {
    if (!value || value === username) {
      setAvailability(null);
      return;
    }

    const errors = validateUsername(value);
    if (errors?.length > 0) {
      setAvailability(null);
      return;
    }

    setIsChecking(true);
    
    // UI-only mockup - simulate availability check
    setTimeout(() => {
      const unavailableUsernames = ['admin', 'user', 'test', 'fortified mind', 'bible_reader'];
      const isAvailable = !unavailableUsernames?.includes(value?.toLowerCase());
      
      setAvailability({
        available: isAvailable,
        message: isAvailable ? 'Username is available!' : 'Username is already taken'
      });
      setIsChecking(false);
    }, 1000);
  };

  const handleUsernameChange = (e) => {
    const value = e?.target?.value;
    setNewUsername(value);
    
    const errors = validateUsername(value);
    setValidationErrors(errors);
    
    if (errors?.length === 0) {
      checkAvailability(value);
    } else {
      setAvailability(null);
    }

    if (isEditing && errors?.length === 0) {
      onUpdate?.(value);
    }
  };

  const getCharacterCount = () => {
    return newUsername?.length || 0;
  };

  const getCharacterCountColor = () => {
    const count = getCharacterCount();
    if (count === 0) return 'text-muted-foreground';
    if (count < 3) return 'text-destructive';
    if (count > 17) return 'text-warning';
    if (count > 20) return 'text-destructive';
    return 'text-success';
  };

  return (
    <div className="bg-card rounded-2xl border border-border shadow-warm-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
          <AtSign className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-heading font-bold text-foreground">Username Settings</h2>
          <p className="text-muted-foreground">Choose how you appear in reflections and community</p>
        </div>
      </div>
      <div className="space-y-4">
        {/* Current Username Display */}
        <div className="bg-muted/40 rounded-lg p-4 border border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="h-8 w-8 bg-primary rounded-full flex items-center justify-center">
                <AtSign className="w-4 h-4 text-primary-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Current Username</p>
                <p className="font-mono text-lg text-foreground">@{username}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Used in reflections</p>
              <p className="text-xs text-muted-foreground">& community features</p>
            </div>
          </div>
        </div>

        {/* Username Input */}
        <div className="space-y-2">
          <label htmlFor="username" className="block text-sm font-medium text-foreground">New Username</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <AtSign className="h-5 w-5 text-muted-foreground" />
            </div>
            <input
              id="username"
              type="text"
              value={newUsername || ''}
              onChange={handleUsernameChange}
              disabled={!isEditing}
              className={`block w-full pl-10 pr-12 py-3 border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:border-primary text-base font-mono ${
                !isEditing 
                  ? 'bg-muted border-border cursor-not-allowed opacity-60'
                  : validationErrors?.length > 0
                  ? 'bg-background border-destructive focus:ring-destructive'
                  : availability?.available === false
                  ? 'bg-background border-warning focus:ring-warning'
                  : availability?.available === true
                  ? 'bg-background border-success focus:ring-success' :'bg-background border-border focus:ring-primary'
              }`}
              placeholder="Enter your username"
              maxLength="20"
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              {isChecking && <Loader className="h-5 w-5 text-muted-foreground animate-spin" />}
              {!isChecking && availability?.available === true && <Check className="h-5 w-5 text-success" />}
              {!isChecking && availability?.available === false && <X className="h-5 w-5 text-destructive" />}
              {!isChecking && validationErrors?.length > 0 && <AlertCircle className="h-5 w-5 text-destructive" />}
            </div>
          </div>

          <div className="flex justify-between items-center">
            <div className="text-xs text-muted-foreground">
              Username will appear as: <span className="font-mono text-foreground">@{newUsername || 'username'}</span>
            </div>
            <div className={`text-xs font-medium ${getCharacterCountColor()}`}>
              {getCharacterCount()}/20 characters
            </div>
          </div>
        </div>

        {/* Validation Messages */}
        {validationErrors?.length > 0 && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <AlertCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                {validationErrors?.map((error, index) => (
                  <p key={index} className="text-sm text-destructive">{error}</p>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Availability Status */}
        {availability && validationErrors?.length === 0 && (
          <div className={`border rounded-lg p-3 ${availability?.available ? 'bg-success/10 border-success/30' : 'bg-warning/10 border-warning/30'}`}>
            <div className="flex items-center space-x-2">
              {availability?.available ? <Check className="w-4 h-4 text-success" /> : <X className="w-4 h-4 text-warning" />}
              <p className={`text-sm font-medium ${availability?.available ? 'text-success' : 'text-warning'}`}>
                {availability?.message}
              </p>
            </div>
          </div>
        )}

        {/* Username Guidelines */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
          <h4 className="font-medium text-foreground mb-2 text-sm">Username Guidelines</h4>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <li>• 3-20 characters long</li>
            <li>• Letters, numbers, underscores</li>
            <li>• No start/end underscore</li>
            <li>• No consecutive underscores</li>
            <li className="col-span-2">• Must be unique across all users</li>
          </ul>
        </div>

        {/* Preview in Community */}
        <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg p-4 border border-primary/20">
          <h4 className="font-medium text-foreground mb-3">Community Preview</h4>
          <div className="bg-card rounded-lg p-3 border border-border">
            <div className="flex items-start space-x-3">
              <div className="h-8 w-8 bg-primary rounded-full flex items-center justify-center">
                <span className="text-sm font-bold text-primary-foreground">
                  {(newUsername || username)?.[0]?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1">
                <p className="font-medium text-foreground">@{newUsername || username}</p>
                <p className="text-sm text-muted-foreground italic">
                  "This verse has brought me so much peace during difficult times. 
                  Thank you, Lord, for your constant presence."
                </p>
                <p className="text-xs text-muted-foreground mt-1">2 hours ago</p>
              </div>
            </div>
          </div>
        </div>

        {!isEditing && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <p className="text-sm text-muted-foreground text-center">
              Click "Edit Profile" above to change your username
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsernameManager;